'use client';

import { c as _c } from "react/compiler-runtime";
import { cx } from '@7f-ui';
import { BaseButton } from '@7f-ui/components/button/button';
import { Fields } from '@feat/feat-search/fields';
import { useSearchField } from '@feat/feat-search/search-form';
import { trackClickCta } from '@feat/feat-tracking/cta';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { shopTestId } from '@utils/utils-shop-test-ids';
import type { Variants } from 'motion/react';
import { AnimatePresence } from 'motion/react';
import * as m from 'motion/react-m';
import dynamic from 'next/dynamic';
import type { ComponentProps } from 'react';
import { useState } from 'react';
const SearchModal = dynamic(() => import('@feat/feat-search/search-modal').then(mod => mod.SearchModal), {
  ssr: false
});
export function SearchField() {
  const $ = _c(49);
  const [hasOpenedModal, setHasOpenedModal] = useState(false);
  const {
    ref,
    min,
    max,
    tabIndex,
    isAnimating,
    startAnimating,
    isNavigating,
    isMobile,
    isModalOpen,
    setIsModalOpen,
    isExpanded,
    setIsExpanded,
    collapseSearchField,
    activeField,
    setActiveField,
    control,
    formState,
    formReset,
    clearErrors,
    initFormData,
    handleSubmit,
    handleLocationChange,
    handleReset,
    handleExpandedComplete,
    handleCollapsedComplete
  } = useSearchField();
  let t0;
  if ($[0] !== initFormData || $[1] !== isExpanded || $[2] !== isMobile || $[3] !== setIsExpanded || $[4] !== startAnimating) {
    t0 = () => {
      if (isMobile) {
        return;
      }
      if (!isExpanded) {
        initFormData();
        startAnimating();
        setIsExpanded(true);
      }
    };
    $[0] = initFormData;
    $[1] = isExpanded;
    $[2] = isMobile;
    $[3] = setIsExpanded;
    $[4] = startAnimating;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  const handleFocus = t0;
  let t1;
  if ($[6] !== initFormData || $[7] !== isExpanded || $[8] !== isMobile || $[9] !== setIsExpanded || $[10] !== setIsModalOpen || $[11] !== startAnimating) {
    t1 = () => {
      if (isMobile) {
        setHasOpenedModal(true);
        setIsModalOpen(true);
        return;
      }
      if (!isExpanded) {
        initFormData();
        startAnimating();
        setIsExpanded(true);
      }
      trackClickCta("open search", {
        type: "search"
      });
    };
    $[6] = initFormData;
    $[7] = isExpanded;
    $[8] = isMobile;
    $[9] = setIsExpanded;
    $[10] = setIsModalOpen;
    $[11] = startAnimating;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  const handleClick = t1;
  let t2;
  if ($[13] !== collapseSearchField || $[14] !== formReset || $[15] !== isAnimating.current) {
    t2 = event => {
      if (isAnimating.current) {
        return;
      }
      if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget)) {
        collapseSearchField();
        formReset();
      }
    };
    $[13] = collapseSearchField;
    $[14] = formReset;
    $[15] = isAnimating.current;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  const handleBlur = t2;
  let t3;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = shopTestId("Homepage.search");
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  let t4;
  if ($[18] !== activeField || $[19] !== clearErrors || $[20] !== control || $[21] !== formState || $[22] !== handleCollapsedComplete || $[23] !== handleExpandedComplete || $[24] !== handleLocationChange || $[25] !== handleReset || $[26] !== isAnimating.current || $[27] !== isExpanded || $[28] !== isNavigating || $[29] !== max || $[30] !== min || $[31] !== setActiveField) {
    t4 = <AnimatePresence mode="popLayout" initial={false}>{isExpanded ? <m.div key="expanded" initial="hidden" animate="visible" exit="hidden" onAnimationComplete={handleExpandedComplete} variants={variants.expanded} className={cx("flex flex-1 items-center", {
        "pointer-events-none": !isExpanded
      })}><Fields activeField={activeField} isNavigating={isNavigating} min={min} max={max} control={control} onChange={handleLocationChange} onReset={handleReset} onInputChange={() => {
          if (formState.errors.location) {
            clearErrors("location");
          }
        }} onLabelClick={setActiveField} onFocus={v => {
          if (isAnimating.current) {
            return;
          }
          setActiveField(v);
        }} /></m.div> : <BaseButton as={m.button} type="button" key="collapsed" initial="hidden" animate="visible" exit="hidden" onAnimationComplete={handleCollapsedComplete} variants={variants.collapsed} className="w-full"><div className="flex items-center gap-2 px-5"><MagnifyingGlass className="size-6 shrink-0 text-secondary-red" weight="bold" /><span className="text-secondary-text">Orte, Städte und Regionen</span></div></BaseButton>}</AnimatePresence>;
    $[18] = activeField;
    $[19] = clearErrors;
    $[20] = control;
    $[21] = formState;
    $[22] = handleCollapsedComplete;
    $[23] = handleExpandedComplete;
    $[24] = handleLocationChange;
    $[25] = handleReset;
    $[26] = isAnimating.current;
    $[27] = isExpanded;
    $[28] = isNavigating;
    $[29] = max;
    $[30] = min;
    $[31] = setActiveField;
    $[32] = t4;
  } else {
    t4 = $[32];
  }
  let t5;
  if ($[33] !== handleBlur || $[34] !== handleClick || $[35] !== handleFocus || $[36] !== handleSubmit || $[37] !== ref || $[38] !== t4 || $[39] !== tabIndex) {
    t5 = <form ref={ref} className="relative flex h-16 rounded-2xl bg-white shadow-elevation3 focus-within:outline-hidden focus-within:ring-0" onFocus={handleFocus} onClick={handleClick} onBlur={handleBlur} tabIndex={tabIndex} onSubmit={handleSubmit} data-testid={t3}>{t4}</form>;
    $[33] = handleBlur;
    $[34] = handleClick;
    $[35] = handleFocus;
    $[36] = handleSubmit;
    $[37] = ref;
    $[38] = t4;
    $[39] = tabIndex;
    $[40] = t5;
  } else {
    t5 = $[40];
  }
  let t6;
  if ($[41] !== hasOpenedModal || $[42] !== isMobile || $[43] !== isModalOpen || $[44] !== setIsModalOpen) {
    t6 = isMobile && hasOpenedModal ? <SearchModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} /> : null;
    $[41] = hasOpenedModal;
    $[42] = isMobile;
    $[43] = isModalOpen;
    $[44] = setIsModalOpen;
    $[45] = t6;
  } else {
    t6 = $[45];
  }
  let t7;
  if ($[46] !== t5 || $[47] !== t6) {
    t7 = <>{t5}{t6}</>;
    $[46] = t5;
    $[47] = t6;
    $[48] = t7;
  } else {
    t7 = $[48];
  }
  return t7;
}
const variants: Record<string, Variants> = {
  expanded: {
    hidden: {
      opacity: 0,
      transition: {
        opacity: {
          duration: 0.3
        }
      }
    },
    visible: {
      opacity: 1,
      transition: {
        opacity: {
          duration: 0.5,
          delay: 0.2
        }
      }
    }
  },
  collapsed: {
    hidden: {
      opacity: 0,
      transition: {
        opacity: {
          duration: 0.3
        }
      }
    },
    visible: {
      opacity: 1,
      transition: {
        opacity: {
          duration: 0.9,
          delay: 0.2
        }
      }
    }
  }
};